import { useReactiveVar } from '@apollo/client';

import { useCallback } from 'react';

import { useMyShareOptionsQuery, useRegisterShareMutation } from 'core/graphql';
import { authState } from 'core/state';
import { isCareerViewer } from 'core/hooks/CareerCenter/isCareerViewer';

export interface IShareOption {
  channel: string;
  text: string;
  targetUrl: string;
  // 🔴 Запрос уходит ТОЛЬКО зрителю карьерного раздела. Без этого условия
  // преподаватель, открывший /growth по прямому адресу, получал пачку
  // молчаливых PermissionDenied и полупустой экран: GraphQL-ошибки в клиенте
  // не редиректят и не показывают тост, поэтому симптом выглядел как «раздел
  // сломан», а в консоли лежала причина, которую никто не читает.
  /** `null` у Instagram: веб-эндпоинта шеринга у него не существует. */
  shareUrl: string | null;
  bonusAvailable: boolean;
  /** Сколько очков даст этот репост. 0 — за него не полагается ничего. */
  bonusPoints: number;
  /** Полагается ли ещё и промокод. */
  bonusPromo: boolean;
}

export interface IShareResult {
  counted: boolean;
  grantedTitles: string[];
  promoCodes: string[];
}

/**
 * ТЗ 0039 шаг 7 — репост.
 *
 * ⚠️ Фиксируется нажатие НАШЕЙ кнопки, а не публикация: у LinkedIn и Facebook
 * нет ни колбэка, ни API проверки поста. Поэтому и подпись в интерфейсе —
 * «спасибо, что поделились», а не «за публикацию».
 */
export const useShare = (language?: string) => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const skip = !isCareerViewer(active_role) || Boolean(isDemoUser);

  const { data, loading, refetch } = useMyShareOptionsQuery({
    variables: { language },
    fetchPolicy: 'cache-and-network',
    skip,
  });
  const [register] = useRegisterShareMutation();

  const registerShare = useCallback(
    async (channel: string): Promise<IShareResult> => {
      const { data: result } = await register({ variables: { channel } });
      const payload = result?.registerShare?.result;
      await refetch();
      return {
        counted: Boolean(payload?.counted),
        grantedTitles: [...(payload?.grantedTitles ?? [])],
        promoCodes: [...(payload?.promoCodes ?? [])],
      };
    },
    [register, refetch],
  );

  return {
    options: (data?.myShareOptions ?? []) as IShareOption[],
    loading,
    registerShare,
  };
};
